import { success, fail } from '@/helpers/result-helper'
import { isSuccess } from '@/helpers/http-status-helpers'
// eslint-disable-next-line no-unused-vars
import CandidateR2WUserDetailsViewModel from '@/models/candidate/candidateR2WUserDetailsViewModel'
import toast from '@services/toasts/index.js'
import CandidateApiErrorFactory from '@/services/candidate/CandidateApiErrorFactory'
import { VuexResponse } from '@/helpers/vuex-action-builder'

const getDefaultState = () => {
  return {
    cached: {
      list: [],
      lastUpdated: null,
      devices: [],
    },
    loadingCount: 0,
    crudLoadingCount: 0,
    userDetailsLoadingCount: 0,
    userDetailsLoadingCrudCount: 0,
  }
}

const state = getDefaultState()

export default {
  namespaced: true,
  state,

  getters: {
    candidatePreferreds: (state) => {
      return state.cached
    },
    isLoadingCandidatePreferreds: (state) => state.loadingCount > 0,
    isLoadingCandidatePreferredsCRUD: (state) => state.crudLoadingCount > 0,
    isLoadingCandidateUserDetails: (state) => state.userDetailsLoadingCount > 0,
    isLoadingCandidateUserDetailsCRUD: (state) =>
      state.userDetailsLoadingCrudCount > 0,
    getCandidateDevices: (state) => (candidateId) => {
      const devices =
        state?.cached?.devices?.find(({ id }) => id === candidateId)?.devices ||
        []

      return devices.slice().sort((a, b) => {
        if (a.isActive && !b.isActive) return -1
        if (!a.isActive && b.isActive) return 1
        return new Date(b.dateUpdatedUtc) - new Date(a.dateUpdatedUtc)
      })
    },
  },

  mutations: {
    START_LOADING(state) {
      state.loadingCount++
    },
    FINISH_LOADING(state) {
      state.loadingCount--
    },
    START_LOADING_CRUD(state) {
      state.crudLoadingCount++
    },
    FINISH_LOADING_CRUD(state) {
      state.crudLoadingCount--
    },
    START_LOADING_USER_DETAILS(state) {
      state.userDetailsLoadingCount++
    },
    FINISH_LOADING_USER_DETAILS(state) {
      state.userDetailsLoadingCount--
    },
    START_LOADING_USER_DETAILS_CRUD(state) {
      state.userDetailsLoadingCrudCount++
    },
    FINISH_LOADING_USER_DETAILS_CRUD(state) {
      state.userDetailsLoadingCrudCount--
    },
    ADD_DEVICES(state, payload) {
      state.cached.devices.push(payload)
    },

    CLEAR_STORE(state) {
      // Resets store to default state
      Object.assign(state, getDefaultState())
    },
    SET_STATE(state, { key, value }) {
      state[key] = value
    },
  },

  actions: {
    async returnGuaranteedList({ commit, getters, dispatch }, payload) {
      const response = await this.$api.candidate.getCandidateGuaranteed(payload)
      if (isSuccess(response.status)) {
        return success(response.data)
      }
    },
    /**
     * Retrieve's a Candidate's Ready2Work user details.
     * @param {object} VuexAction
     * @param {number} candidateId
     * @returns {{ isSuccess: boolean, data: CandidateR2WUserDetailsViewModel}}
     */
    async getUserDetails({ commit }, candidateId) {
      try {
        commit('START_LOADING_USER_DETAILS')

        const response = await this.$api.candidate.getUserDetails(candidateId)

        if (isSuccess(response.status)) return success(response.data)
      } finally {
        commit('FINISH_LOADING_USER_DETAILS')
      }
    },
    /**
     * Creates a Ready2Work login for a Candidate.
     * @param {object} VuexAction
     * @param {{ candidateId: number, password: string }}
     * @returns {{ isSuccess: boolean }}
     */
    async createLogin({ commit }, { candidateId }) {
      try {
        commit('START_LOADING_USER_DETAILS_CRUD')

        const response = await this.$api.candidate.createLogin(candidateId)

        if (isSuccess(response.status)) {
          toast.success('Login successfully created.')
          return success()
        }
      } catch (e) {
        const errorMessage = CandidateApiErrorFactory(e.response.data)
        toast.error(errorMessage)

        return fail()
      } finally {
        commit('FINISH_LOADING_USER_DETAILS_CRUD')
      }
    },
    /**
     * Synchronises and Candidate's login email to their email.
     * @param {object} VuexAction
     * @param {number} candidateId
     * @returns {{ isSuccess: boolean }}
     */
    async syncLoginEmail({ commit }, candidateId) {
      try {
        commit('START_LOADING_USER_DETAILS_CRUD')

        const response = await this.$api.candidate.syncLoginEmail(candidateId)

        if (isSuccess(response.status)) {
          toast.success('Login email address has been updated.')
          return success()
        }
      } catch (e) {
        const errorMessage = CandidateApiErrorFactory(e.response.data)
        toast.error(errorMessage)

        return fail()
      } finally {
        commit('FINISH_LOADING_USER_DETAILS_CRUD')
      }
    },
    /**
     * Removes a Candidate's Ready2Work login.
     * @param {object} VuexAction
     * @param {number} candidateId
     * @returns {{ isSuccess: boolean }}
     */
    async removeLogin({ commit }, candidateId) {
      try {
        commit('START_LOADING_USER_DETAILS_CRUD')

        const response = await this.$api.candidate.removeLogin(candidateId)

        if (isSuccess(response.status)) {
          toast.success('Ready2Work login has been removed.')
          return success()
        }
      } catch (e) {
        const errorMessage = CandidateApiErrorFactory(e.response.data)
        toast.error(errorMessage)

        return fail()
      } finally {
        commit('FINISH_LOADING_USER_DETAILS_CRUD')
      }
    },
    /**
     * Requests to reset a candidate's password.
     * @param {object} VuexAction
     * @param {number} candidateId
     * @returns {{ isSuccess: boolean }}
     */
    async resetPassword({ commit }, candidateId) {
      try {
        commit('START_LOADING_USER_DETAILS_CRUD')

        const response = await this.$api.candidate.resetPassword(candidateId)

        if (isSuccess(response.status)) {
          toast.success('Password reset email sent.')
          return success()
        }
      } catch (e) {
        const errorMessage = CandidateApiErrorFactory(e.response.data)
        toast.error(errorMessage)

        return fail()
      } finally {
        commit('FINISH_LOADING_USER_DETAILS_CRUD')
      }
    },

    async getRegisteredDevices({ commit }, candidateId) {
      const response = await this.$api.devices.getDevicesByCandidate(
        candidateId
      )
      commit('ADD_DEVICES', { id: candidateId, devices: response.data })
    },

    async getBullhornData({ commit, dispatch }, candidateId) {
      return await new VuexResponse(commit, dispatch)
        .request(() => this.$api.candidate.getBullhornData(candidateId))
        .withFailureToast('Failed to bullhorn data')
        .go()
    },
    /**
     * Completes a candidate's registration interview process.
     * @param {{ candidateId: number, registrationId: number }}
     */
    async completeRegistrationInterview(
      { commit, dispatch },
      { candidateId, registrationId }
    ) {
      return await new VuexResponse(commit, dispatch)
        .request(() =>
          this.$api.candidate.completeRegistrationInterview(
            candidateId,
            registrationId
          )
        )
        .withSuccessToast(
          this.$i18n.t('forms.alerts.successCompleteRegistration')
        )
        .withFailureToast(this.$i18n.t('forms.alerts.failCompleteRegistration'))
        .go()
    },
    /**
     * Retrieves a candidate's registration step submission data.
     * @param {{ candidateId: number, registrationId: number, stepCodeName: string }}
     */
    async getRegistrationStepSubmission(
      { commit, dispatch },
      { candidateId, registrationId, stepCodeName }
    ) {
      return await new VuexResponse(commit, dispatch)
        .request(() =>
          this.$api.candidate.getRegistrationStepSubmission(
            candidateId,
            registrationId,
            stepCodeName
          )
        )
        .go()
    },
  },
}
